import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "photo-upload-form__file-text" }
const _hoisted_2 = { class: "photo-upload-form__file-title" }
const _hoisted_3 = { class: "photo-upload-form__file-subtitle" }
const _hoisted_4 = {
  key: 0,
  class: "photo-upload-form__error"
}
const _hoisted_5 = {
  key: 1,
  class: "photo-upload-form__preview-grid"
}
const _hoisted_6 = ["src", "alt"]
const _hoisted_7 = {
  key: 1,
  class: "photo-upload-form__preview-video"
}
const _hoisted_8 = ["onClick", "aria-label"]
const _hoisted_9 = ["title"]
const _hoisted_10 = {
  key: 2,
  class: "photo-upload-form__client-toggle"
}
const _hoisted_11 = { class: "photo-upload-form__client-toggle-label" }
const _hoisted_12 = { class: "photo-upload-form__client-toggle-control" }
const _hoisted_13 = { class: "toggle-switch" }
const _hoisted_14 = { class: "photo-upload-form__actions" }
const _hoisted_15 = {
  key: 0,
  class: "pi pi-cloud-upload",
  "aria-hidden": "true"
}

import { ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import AppButton from '../../base/AppButton.vue';
import AppModal from '../../base/Modal.vue';
import { sanitizeFileNames } from '@/utils/fileNameSanitizer';

interface Props {
  show: boolean;
  uploading?: boolean;
  showClientVisibilityToggle?: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'upload', payload: { files: File[]; sharedWithClient: boolean }): void;
}

interface PreviewItem {
  file: File;
  url: string;
  name: string;
  isImage: boolean;
}

const MAX_FILES = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'PhotoUploadModal',
  props: {
    show: { type: Boolean, default: false },
    uploading: { type: Boolean, default: false },
    showClientVisibilityToggle: { type: Boolean, default: false }
  },
  emits: ["close", "upload"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n();

const previewItems = ref<PreviewItem[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const sharedWithClient = ref(true);
const fileError = ref('');

const addFiles = (files: File[]) => {
  const media = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
  const sanitized = sanitizeFileNames(media);
  const remaining = MAX_FILES - previewItems.value.length;
  const toAdd = sanitized.slice(0, remaining);
  if (sanitized.length > remaining) {
    fileError.value = t('documents.tooManyFiles', { max: MAX_FILES });
  }
  for (const file of toAdd) {
    previewItems.value.push({
      file,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      name: file.name,
      isImage: file.type.startsWith('image/'),
    });
  }
};

const removeFile = (index: number) => {
  const item = previewItems.value[index];
  if (item.url) URL.revokeObjectURL(item.url);
  previewItems.value.splice(index, 1);
  fileError.value = '';
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target.files) return;
  addFiles(Array.from(target.files));
  target.value = '';
};

const handleDragOver = () => { isDragging.value = true; };
const handleDragLeave = () => { isDragging.value = false; };
const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  if (!event.dataTransfer) return;
  addFiles(Array.from(event.dataTransfer.files));
};

const handleSubmit = () => {
  if (previewItems.value.length === 0 || props.uploading) return;
  emit('upload', {
    files: previewItems.value.map(p => p.file),
    sharedWithClient: sharedWithClient.value,
  });
};

const resetForm = () => {
  for (const item of previewItems.value) {
    if (item.url) URL.revokeObjectURL(item.url);
  }
  previewItems.value = [];
  isDragging.value = false;
  sharedWithClient.value = true;
  fileError.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

watch(
  () => props.show,
  (newVal, oldVal) => { if (newVal && !oldVal) resetForm(); }
);

onBeforeUnmount(() => {
  for (const item of previewItems.value) {
    if (item.url) URL.revokeObjectURL(item.url);
  }
});

const handleClose = () => {
  resetForm();
  emit('close');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AppModal, {
    show: _ctx.show,
    onClose: handleClose,
    title: _ctx.$t('projects.uploadPhotos'),
    size: "medium",
    "show-close": !_ctx.uploading
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_14, [
        _createVNode(AppButton, {
          type: "button",
          variant: "outline",
          onClick: handleClose,
          disabled: _ctx.uploading
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.cancel')), 1)
          ]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(AppButton, {
          type: "button",
          variant: "primary",
          onClick: handleSubmit,
          disabled: previewItems.value.length === 0 || _ctx.uploading,
          loading: _ctx.uploading
        }, {
          default: _withCtx(() => [
            (!_ctx.uploading)
              ? (_openBlock(), _createElementBlock("i", _hoisted_15))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('documents.upload')), 1)
          ]),
          _: 1
        }, 8, ["disabled", "loading"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
        class: "photo-upload-form"
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["photo-upload-form__file-area", { 'photo-upload-form__file-area--dragging': isDragging.value }]),
          onClick: _cache[0] || (_cache[0] = () => fileInput.value?.click()),
          onDragover: _withModifiers(handleDragOver, ["prevent"]),
          onDragleave: _withModifiers(handleDragLeave, ["prevent"]),
          onDrop: _withModifiers(handleDrop, ["prevent"])
        }, [
          _createElementVNode("input", {
            type: "file",
            ref_key: "fileInput",
            ref: fileInput,
            onChange: handleFileChange,
            class: "photo-upload-form__file-input",
            accept: "image/*,video/*",
            multiple: ""
          }, null, 544),
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "photo-upload-form__file-icon" }, [
            _createElementVNode("i", { class: "pi pi-images" })
          ], -1)),
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("p", _hoisted_2, _toDisplayString(previewItems.value.length > 0
                ? `${previewItems.value.length} ${_ctx.$t('documents.filesSelected')}`
                : _ctx.$t('projects.dropPhotosHere')), 1),
            _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t('projects.uploadFormatsHint')), 1)
          ])
        ], 34),
        (fileError.value)
          ? (_openBlock(), _createElementBlock("small", _hoisted_4, _toDisplayString(fileError.value), 1))
          : _createCommentVNode("", true),
        (previewItems.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewItems.value, (item, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "photo-upload-form__preview-item"
                }, [
                  (item.isImage)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: item.url,
                        class: "photo-upload-form__preview-thumb",
                        alt: item.name
                      }, null, 8, _hoisted_6))
                    : (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("i", { class: "pi pi-video" }, null, -1)
                      ]))])),
                  _createElementVNode("button", {
                    type: "button",
                    class: "photo-upload-form__preview-remove",
                    onClick: _withModifiers(($event: any) => (removeFile(index)), ["stop"]),
                    "aria-label": _ctx.$t('documents.removeFile')
                  }, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("i", { class: "pi pi-times" }, null, -1)
                  ]))], 8, _hoisted_8),
                  _createElementVNode("span", {
                    class: "photo-upload-form__preview-name",
                    title: item.name
                  }, _toDisplayString(item.name), 9, _hoisted_9)
                ]))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (_ctx.showClientVisibilityToggle)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t('projects.shareWithClient')), 1),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("label", _hoisted_13, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sharedWithClient).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, sharedWithClient.value]
                  ]),
                  _cache[6] || (_cache[6] = _createElementVNode("span", { class: "toggle-slider" }, null, -1))
                ]),
                _createElementVNode("span", {
                  class: _normalizeClass(["photo-upload-form__toggle-hint", sharedWithClient.value ? 'hint-visible' : 'hint-hidden'])
                }, _toDisplayString(sharedWithClient.value ? _ctx.$t('projects.sharedWithClient') : _ctx.$t('projects.hiddenFromClient')), 3)
              ])
            ]))
          : _createCommentVNode("", true)
      ], 32)
    ]),
    _: 1
  }, 8, ["show", "title", "show-close"]))
}
}

})